import { ESTUDIO } from "../data/estudio";
import { PRECIOS, formatCLP } from "../data/precios";
import { SEGMENTOS, desdeIvaIncluido, precioDesde } from "../data/segmentos";

export const SITE_URL = "https://www.omegadigitalchile.cl";
export const SITE_NAME = ESTUDIO.nombre;
export const SITE_OG_IMAGE = `${SITE_URL}/og-omega.png`;
/* El logotipo y la tarjeta de enlace son cosas distintas: `logo` debe ser el
   logotipo, no una tarjeta con texto encima. Google usa `logo` en resultados. */
export const SITE_LOGO = `${SITE_URL}/logo-omega-digital.png`;
export const SITE_TAGLINE =
  "Guardamos tus emociones en imágenes maravillosas. Fotografía de eventos en Santiago.";
export const PHONE = `+${ESTUDIO.whatsapp}`;
export const EMAIL = ESTUDIO.email;

export const DEFAULT_KEYWORDS = [
  "fotógrafo de eventos Santiago",
  "fotógrafo baby shower Santiago",
  "fotógrafo revelación de sexo",
  "fotógrafo de bodas Santiago",
  "fotógrafo boda civil Santiago",
  "sesión de fotos embarazada",
  "fotógrafo de graduación Santiago",
  "fotografía gastronómica Santiago",
  "fotografía marca personal Santiago",
  "fotógrafo deportivo Santiago",
  "sesión de fotos precio Chile",
  "estudio fotográfico Santiago",
];

/**
 * Nodo de la ficha de negocio que se añade desde JavaScript.
 *
 * La identidad base — @type, nombre, teléfono, dirección, geo — ya viaja
 * estática en el @graph de public/index.html, para que exista sin JS. Volver a
 * declararla aquí emitía el mismo nodo dos veces en la página.
 *
 * Este nodo solo lleva el DELTA que depende de los datos y que por lo tanto no
 * se puede congelar en el HTML: el catálogo con el precio de los dos
 * servicios, las comunas y el horario. Schema.org fusiona nodos que comparten
 * @id, así que este se suma al estático en vez de competir con él, y el
 * catálogo nunca se desvía de precios.js.
 */
export const organizationJsonLd = () => ({
  "@context": "https://schema.org",
  "@id": `${SITE_URL}/#organization`,
  slogan: SITE_TAGLINE,
  knowsAbout: DEFAULT_KEYWORDS,
  areaServed: [
    { "@type": "Country", name: "Chile" },
    { "@type": "City", name: ESTUDIO.ciudad },
    ...ESTUDIO.comunasDestacadas.map((comuna) => ({
      "@type": "AdministrativeArea",
      name: comuna,
    })),
  ],
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    opens: ESTUDIO.horario.desde,
    closes: ESTUDIO.horario.hasta,
  },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Fotografía profesional Omega Digital",
    itemListElement: [
      ...SEGMENTOS.map((segmento) => ({
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: segmento.nombre,
          description: segmento.promesa,
          url: `${SITE_URL}${segmento.path}`,
        },
        price: String(precioDesde(segmento)),
        priceCurrency: "CLP",
        priceSpecification: desdeIvaIncluido(segmento)
          ? undefined
          : { "@type": "PriceSpecification", valueAddedTaxIncluded: false },
      })),
    ],
  },
});

/** Ficha completa. Solo para revisar o regenerar el bloque estático de index.html. */
export const organizationCompletaJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": ["PhotographyBusiness", "LocalBusiness", "Organization"],
  "@id": `${SITE_URL}/#organization`,
  name: SITE_NAME,
  legalName: SITE_NAME,
  url: SITE_URL,
  logo: SITE_LOGO,
  image: SITE_OG_IMAGE,
  telephone: PHONE,
  email: EMAIL,
  slogan: SITE_TAGLINE,
  sameAs: [ESTUDIO.instagram, ESTUDIO.facebook],
  description:
    "Fotografía de eventos y fotos en estudio en Santiago. Precio publicado. Emite factura.",
  address: {
    "@type": "PostalAddress",
    addressLocality: ESTUDIO.ciudad,
    addressRegion: ESTUDIO.region,
    addressCountry: "CL",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: -33.4489,
    longitude: -70.6693,
  },
  areaServed: [
    { "@type": "Country", name: "Chile" },
    { "@type": "City", name: ESTUDIO.ciudad },
    ...ESTUDIO.comunasDestacadas.map((comuna) => ({
      "@type": "AdministrativeArea",
      name: comuna,
    })),
  ],
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    opens: ESTUDIO.horario.desde,
    closes: ESTUDIO.horario.hasta,
  },
  priceRange: "$$",
  currenciesAccepted: "CLP",
  paymentAccepted: ESTUDIO.emiteFactura ? "Invoice, Bank Transfer" : "Bank Transfer",
  knowsAbout: DEFAULT_KEYWORDS,
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Fotografía profesional Omega Digital",
    itemListElement: SEGMENTOS.map((segmento) => {
      return {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: segmento.nombre,
          description: segmento.promesa,
          url: `${SITE_URL}${segmento.path}`,
        },
        price: String(precioDesde(segmento)),
        priceCurrency: "CLP",
        priceSpecification: desdeIvaIncluido(segmento)
          ? undefined
          : { "@type": "PriceSpecification", valueAddedTaxIncluded: false },
      };
    }),
  },
});

export const websiteJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: SITE_URL,
  name: SITE_NAME,
  inLanguage: "es-CL",
  publisher: { "@id": `${SITE_URL}/#organization` },
});

export const breadcrumbJsonLd = (crumbs) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: crumbs.map((crumb, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: crumb.name,
    item: crumb.path.startsWith("http") ? crumb.path : `${SITE_URL}${crumb.path}`,
  })),
});

export const faqJsonLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: { "@type": "Answer", text: item.a },
  })),
});

export const segmentoOffersJsonLd = (segmento) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: segmento.nombre,
  description: segmento.promesa,
  url: `${SITE_URL}${segmento.path}`,
  serviceType: "Fotografía profesional",
  provider: { "@id": `${SITE_URL}/#organization` },
  areaServed: { "@type": "City", name: ESTUDIO.ciudad },
  offers: segmento.packs.map((pack) => ({
    "@type": "Offer",
    name: pack.nombre,
    description: pack.incluye.join(". "),
    price: String(PRECIOS[pack.priceKey]),
    priceCurrency: "CLP",
    availability: "https://schema.org/InStock",
    url: `${SITE_URL}${segmento.path}#packs`,
  })),
});

export const PAGES = {
  home: {
    title: `Fotógrafo de bodas y eventos en Santiago: boda civil, baby shower y revelación | Desde ${formatCLP(PRECIOS.horaYMedia)}`,
    description: `Fotógrafo de bodas, boda civil, baby showers y revelaciones de sexo en Santiago desde ${formatCLP(
      PRECIOS.horaYMedia
    )}. Packs con precio publicado. Guardamos tus emociones en imágenes maravillosas.`,
    keywords: DEFAULT_KEYWORDS.join(", "),
    path: "/",
  },
  precios: {
    title: `Precios de fotógrafo de bodas y eventos en Santiago | Desde ${formatCLP(PRECIOS.horaYMedia)} | Omega Digital`,
    description: `Desde ${formatCLP(
      PRECIOS.horaYMedia
    )} el pack de entrada. Packs de fotografía para eventos y bodas en Santiago de 1 h 30, 2 y 4 horas, sesiones en estudio con precio cerrado y fotos para emprendedores y deporte por bloques de 10. Factura incluida.`,
    keywords: "sesión de fotos precio Chile, precio fotógrafo eventos, precio sesión de fotos en estudio, fotógrafo por hora",
    path: "/precios",
  },
  portafolio: {
    title: "Portafolio de fotografía | Omega Digital Santiago",
    description: "Trabajos reales de Omega Digital: bodas, eventos y sesiones en estudio en Santiago.",
    keywords: "portafolio fotografía Santiago, fotógrafo de eventos Santiago, fotos en estudio",
    path: "/portafolio",
  },
  estudio: {
    title: "Nosotros | Omega Digital Santiago",
    description:
      "Omega Digital fotografía eventos y sesiones en estudio en Santiago y la Región Metropolitana. Emite factura. Precio publicado.",
    keywords: "fotógrafo Santiago, estudio fotográfico Santiago",
    path: "/sobre-omega",
  },
  contacto: {
    title: "Contacto | Omega Digital",
    description: `Escríbenos por WhatsApp al ${ESTUDIO.telefonoDisplay} o al correo ${ESTUDIO.email}. Respondemos en horario hábil.`,
    keywords: "contacto fotógrafo Santiago, reservar fotógrafo eventos, reservar sesión en estudio",
    path: "/contacto",
  },
};
